/**
 * The virtual account's Meetings & summaries tab (TMMAPP-0108; audit 06 § A11, the 0103 review
 * amendment; v2 L622–681): the Summary card, then the sub-tabs Upcoming / Past (+ Calendar, D1) with
 * an outlined "+ NEW MEETING" for a writer, and one list card of 0103's `MeetingRow`s. The row
 * actions, the editor and every confirmation come from `useMeetingActions`, the one copy My Meetings
 * uses too; the calendar is My Meetings' `MeetingsCalendarTab` on this virtual account. The sub-tab
 * is in the URL (`mt`).
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Button, Paper, Stack, Tab, Tabs, Typography, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { meetingsApi, type MeetingCalendarEntry } from '@/api/meetings';
import { ErrorState } from '@/components/ErrorState';
import { AddIcon } from '@/components/icons';
import { MeetingRow } from '@/features/meetings/MeetingRow';
import { MeetingsCalendarTab } from '@/features/meetings/MeetingsCalendarTab';
import { VirtualAccountSummaryPanel } from '@/features/meetings/VirtualAccountSummaryPanel';
import { nowMs, pastRange, splitMeetings, upcomingRange } from '@/features/meetings/meetingLists';
import { useMeetingActions } from '@/features/meetings/useMeetingActions';
import { todayCivilInZone } from '@/i18n/zonedCivil';
import { useMe } from '@/session/meHooks';
import { useUrlState } from '@/shared/useUrlState';
import { resolveTokens } from '@/theme/theme';
import { MEETINGS_URL, type MeetingTab } from './meetingsUrl';

interface MeetingsSummariesTabProps {
  virtualAccountId: number;
  /** Writes meetings and the summary (the owner before TMMAPP-0110). */
  canWrite: boolean;
  onNotify: (message: string) => void;
}

export function MeetingsSummariesTab({
  virtualAccountId,
  canWrite,
  onNotify,
}: MeetingsSummariesTabProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const today = todayCivilInZone(me.user.timezone ?? 'UTC');
  const [{ mt }, setUrl] = useUrlState(MEETINGS_URL);
  const [entries, setEntries] = useState<MeetingCalendarEntry[] | null>(null);
  const [failure, setFailure] = useState<{ error: unknown } | null>(null);

  const load = useCallback(async () => {
    try {
      const [ahead, behind] = await Promise.all([
        meetingsApi.listForVirtualAccount(virtualAccountId, upcomingRange(today)),
        meetingsApi.listForVirtualAccount(virtualAccountId, pastRange(today)),
      ]);
      setEntries([...ahead.items, ...behind.items]);
      setFailure(null);
    } catch (error) {
      setFailure({ error });
    }
  }, [virtualAccountId, today]);

  const meetings = useMeetingActions({ writer: () => canWrite, reload: load });

  const lastFetch = useRef<string | null>(null);
  useEffect(() => {
    const key = `${virtualAccountId}:${today}`;
    if (lastFetch.current === key) return;
    lastFetch.current = key;
    void load();
  }, [virtualAccountId, today, load]);

  const { upcoming, past } = splitMeetings(entries ?? [], nowMs());

  function list(rows: MeetingCalendarEntry[], empty: string) {
    if (failure !== null) {
      return (
        <ErrorState
          variant="section"
          error={failure.error}
          what={t('errors.what.virtualAccountMeetings')}
          onRetry={() => void load()}
        />
      );
    }
    if (entries === null) return <Typography>{t('common.loading')}</Typography>;
    return (
      <Paper sx={{ p: 0, overflow: 'hidden' }} data-meetings-list={mt}>
        {rows.length === 0 ? (
          <Typography
            sx={{
              p: '26px 20px',
              fontSize: 13,
              lineHeight: 1.6,
              maxWidth: 520,
              color: tokens.text2,
            }}
          >
            {empty}
          </Typography>
        ) : (
          rows.map((entry, index) => (
            <MeetingRow
              key={`${entry.meetingId}-${entry.occurrenceKey}`}
              entry={entry}
              onOpen={() => void meetings.openEditor(entry)}
              actions={meetings.actionsFor(entry)}
              last={index === rows.length - 1}
            />
          ))
        )}
      </Paper>
    );
  }

  return (
    <Stack sx={{ gap: 2.5 }}>
      <VirtualAccountSummaryPanel
        virtualAccountId={virtualAccountId}
        owner={canWrite}
        onNotify={onNotify}
      />
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Tabs
            value={mt}
            onChange={(_event, next: MeetingTab) => setUrl({ mt: next })}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            aria-label={t('virtualAccount.tab.meetings')}
          >
            <Tab value="upcoming" label={t('virtualAccount.meetings.upcoming')} />
            <Tab value="past" label={t('virtualAccount.meetings.past')} />
            <Tab value="calendar" label={t('meetings.calendar')} />
          </Tabs>
        </Box>
        {canWrite && (
          <Button
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={() => meetings.openNew({ virtualAccountId })}
          >
            {t('meetings.new')}
          </Button>
        )}
      </Stack>
      {mt === 'upcoming' && list(upcoming, t('virtualAccount.meetings.emptyUpcoming'))}
      {mt === 'past' && list(past, t('virtualAccount.meetings.emptyPast'))}
      {mt === 'calendar' && <MeetingsCalendarTab virtualAccountId={virtualAccountId} />}
      {meetings.node}
    </Stack>
  );
}
